/**
 * RouteHead.jsx — TabedLab
 *
 * Declarative per-route SEO using react-helmet-async.
 * Lives INSIDE <BrowserRouter> (uses useLocation), and emits
 * the correct <link rel="canonical">, <title>, meta description,
 * og:* and twitter:* tags for the current pathname.
 *
 * Pages that render their own <SEO> component will further override
 * specific tags via Helmet's last-write-wins behavior — that's intentional.
 *
 * Usage (already wired in App.js):
 *   <BrowserRouter>
 *     <RouteHead />
 *     <Routes>...</Routes>
 *   </BrowserRouter>
 */

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

// ─── Site-wide defaults ──────────────────────────────────────────────────────
const SITE_BASE_URL    = 'https://tabedlab.com';
const DEFAULT_TITLE    = 'AI Spectral & Chromatographic Analysis | TabedLab';
const DEFAULT_DESC     = 'Analyze FTIR and HPLC data with AI in under 30 seconds. '
                       + 'Peak detection, functional group identification, database comparison '
                       + '(NIST, SDBS, PubChem) and publication-ready PDF reports.';
const DEFAULT_OG_IMAGE = `${SITE_BASE_URL}/og-image.png`;

// ─── Per-route metadata ──────────────────────────────────────────────────────
// Keys are exact React Router pathnames.
const ROUTE_META = {
  '/': {
    title:       DEFAULT_TITLE,
    description: DEFAULT_DESC,
  },
  '/demo': {
    title:       'Free Spectral Analysis Demo | TabedLab',
    description: 'Try TabedLab free — upload a FTIR or HPLC file and '
                + 'receive a full AI interpretation in under 30 seconds. No account required.',
  },
  '/about': {
    title:       'About TabedLab | AI-Powered Spectral Analysis',
    description: 'Learn about the team and mission behind TabedLab — '
                + 'making AI-powered spectral and chromatographic analysis accessible to every laboratory.',
  },
  '/sign-up': {
    title:       'Create Your Free Account | TabedLab',
    description: 'Sign up for TabedLab and unlock more spectral analyses, '
                + 'PDF report downloads and analysis history.',
  },
  // Internal alias used by existing CTAs/forms — same content as /sign-up
  '/register': {
    title:       'Create Your Free Account | TabedLab',
    description: 'Sign up for TabedLab and unlock more spectral analyses, '
                + 'PDF report downloads and analysis history.',
  },
  '/privacy-policy': {
    title:       'Privacy Policy | TabedLab',
    description: 'How TabedLab handles your data: uploaded files are never used to train AI models '
                + 'and all transfers are encrypted with TLS 1.3.',
  },
  '/terms': {
    title:       'Terms of Service | TabedLab',
    description: 'TabedLab terms of service — usage conditions for the AI spectral analysis platform.',
  },
  '/faq': {
    title:       'FAQ — Frequently Asked Questions | TabedLab',
    description: 'Answers to common questions about TabedLab: supported file formats, '
                + 'analysis accuracy, data privacy, and more.',
  },
  '/contact': {
    title:       'Contact Us | TabedLab',
    description: 'Get in touch with the TabedLab team for support, partnership enquiries or feedback.',
  },
  '/login': {
    title:       'Log In | TabedLab',
    description: 'Log in to your TabedLab account to access your spectral analyses and reports.',
  },
  '/blog': {
    title:       'Blog — Spectroscopy & Chromatography Insights | TabedLab',
    description: 'Technical articles, tutorials and case studies on AI-powered spectroscopy and chromatography.',
  },
  '/compare': {
    title:       'Compare Multiple Spectra | TabedLab',
    description: 'Overlay and compare multiple spectra (FTIR, HPLC) to detect peak shifts, '
                + 'chemical changes and contamination.',
  },
};

// Routes that should NOT be indexed (auth, transactional)
const NOINDEX_PREFIXES = [
  '/dashboard',
  '/history',
  '/analysis/',
  '/subscription',
  '/admin',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
];

const RouteHead = () => {
  const { pathname } = useLocation();
  const canonicalUrl = `${SITE_BASE_URL}${pathname}`;
  const meta         = ROUTE_META[pathname] || {};
  const title        = meta.title       || DEFAULT_TITLE;
  const description  = meta.description || DEFAULT_DESC;
  const ogImage      = DEFAULT_OG_IMAGE;
  const shouldNoIndex = NOINDEX_PREFIXES.some(p => pathname.startsWith(p));

  return (
    <Helmet>
      <title>{title}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={description} />

      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {shouldNoIndex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
};

export default RouteHead;
